import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useReactToPrint } from 'react-to-print'
import Sheet from '../../components/Sheet'
import ActionBar from '../../components/ActionBar'
import Tags from './Tags'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import BarcodeScanner, { isCameraAvailable } from '../../components/BarcodeScanner'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'

export default function PrintOtherTags(props) {
    const { t } = useTranslation()
    const { showPrintOtherTags, setShowPrintOtherTags, fetchResource } = props
    const [barcodes, setBarcodes] = useState([''])
    const [barcodesList, setBarcodesList] = useState('')
    // The paste-a-list mode is half-built: its checkbox is still commented out
    // below, so this never becomes true. The branch is kept rather than deleted
    // because the stub is deliberate, not leftover.
    const [codesAreList, setCodesAreList] = useState(false)
    const [tagInfo, setTagInfo] = useState({ products: [], resolve: undefined })
    const [showScanner, setShowScanner] = useState(false)
    const [scanFeedback, setScanFeedback] = useState(null)
    const [lookingUp, setLookingUp] = useState(false)
    const componentRef = useRef()

    function handleAddBarcode() {
        setBarcodes([...barcodes, ''])
    }

    function handleRemoveBarcode(index) {
        if (barcodes.length === 1) return setBarcodes([''])
        const currBarcodes = [...barcodes]
        currBarcodes.splice(index, 1)
        setBarcodes(currBarcodes)
    }

    function handleInputChange(e, index) {
        if (e.target.value.length >= 9) return
        const currBarcodes = [...barcodes]
        currBarcodes[index] = e.target.value
        setBarcodes(currBarcodes)
    }

    async function handleScan(code) {
        // Pause while the lookup is in flight so scans cannot stack up.
        setLookingUp(true)
        try {
            // A tag left sitting in frame re-fires this every ~1.5s (the dedup
            // window) for as long as it's in view, indefinitely - so a full
            // products/get-product round-trip here, purely to name the item in
            // the toast, is store-wifi bandwidth spent on every repeat, not just
            // once. The code is already known to be a duplicate; say that.
            if (barcodes.includes(code)) {
                return setScanFeedback({
                    type: 'warning',
                    message: `Already added: ${code}`
                })
            }

            const response = await fetchResource({
                resource: 'products/get-product',
                body: { barcode: code }
            })

            if (response.error) {
                return setScanFeedback({ type: 'error', message: `Barcode ${code} not found` })
            }

            const { title, price } = response.product
            // Replace the trailing empty row rather than leaving a gap.
            setBarcodes(prev => {
                const next = prev.filter(entry => entry !== '')
                return [...next, code, '']
            })
            setScanFeedback({ type: 'success', message: `Added: ${title} — $${price}` })
        } finally {
            setLookingUp(false)
        }
    }

    useEffect(() => {
        if (tagInfo.resolve) tagInfo.resolve()
    }, [tagInfo])

    // The hook rather than the <ReactToPrint> component. The component supplies its
    // print button through a render-prop `trigger` that must return the element it
    // attaches its own handler to -- which meant this one action row could not be
    // built by ActionBar, and was the last place in the app still hand-matching the
    // button vocabulary. A hook hands back a plain handler instead, so Print is an
    // ActionBar confirm like every other confirm.
    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        onBeforeGetContent: () =>
            new Promise(async resolve => {
                const codes = (codesAreList
                    ? barcodesList.split('\n').map(code => code.trim())
                    : barcodes
                ).filter(code => code !== '')
                const productsResponse = await fetchResource({
                    resource: 'products/get-tag-info',
                    body: { barcodes: codes },
                    blocking: true
                })

                if (productsResponse.error) return alert(productsResponse.error)
                setTagInfo({ products: productsResponse.products, resolve })
            })
    })

    return (
        <>
            <Sheet
                open={showPrintOtherTags}
                onClose={() => setShowPrintOtherTags(false)}
                title={t('products.printTags')}
                actions={
                    <ActionBar
                        cancel={{
                            label: t('common.cancel'),
                            onClick: () => setShowPrintOtherTags(false)
                        }}
                        secondary={isCameraAvailable() ? {
                            label: t('common.scan'),
                            onClick: () => setShowScanner(true),
                            startIcon: <QrCodeScannerIcon />
                        } : null}
                        confirm={{ label: t('products.printTags'), onClick: handlePrint }}
                    />
                }
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {!codesAreList &&
                        barcodes.map((barcode, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {/* Full width. The field was capped at 133px and
                                    indented 24px inside a 350px box, so an 8-digit
                                    number sat in roughly a third of the row. */}
                                <TextField
                                    autoFocus
                                    fullWidth
                                    value={barcode}
                                    type='number'
                                    // Every row used to hardcode id='barcode', so the
                                    // ids collided and a row's label focused the first
                                    // row's field rather than its own.
                                    id={`barcode-${index}`}
                                    label={t('products.barcodeLabel')}
                                    onChange={e => handleInputChange(e, index)}
                                />
                                <IconButton
                                    aria-label={t('products.removeBarcode')}
                                    onClick={() => handleRemoveBarcode(index)}
                                    sx={{ flex: 'none', width: 44, height: 44 }}
                                >
                                    <RemoveCircleIcon />
                                </IconButton>
                            </Box>
                        ))}
                    {codesAreList && (
                        <TextField
                            autoFocus
                            fullWidth
                            type='number'
                            id='barcode-list'
                            label={t('products.barcodeListLabel')}
                            value={barcodesList}
                            onChange={e => setBarcodesList(e.target.value)}
                            multiline
                        />
                    )}
                    {/* One Add at the foot of the list. It used to appear on whichever
                        row happened to be last and vanish from it the moment another
                        was added, which made the control move every time it was used. */}
                    <Button
                        onClick={handleAddBarcode}
                        startIcon={<AddCircleIcon />}
                        sx={{ alignSelf: 'flex-start', minHeight: 44 }}
                    >
                        {t('products.addBarcode')}
                    </Button>
                    {/* <FormControlLabel
                        id='codes-are-list'
                        control={<Checkbox />}
                        label={t('products.listLabel')}
                        onChange={e => {
                            setCodesAreList(e.target.checked)
                        }}
                    /> */}
                </Box>
            </Sheet>

            {/* Outside the sheet: react-to-print reaches this by ref while printing,
                so it must not be inside anything that unmounts with the form. */}
            <div style={{ display: 'none' }}>
                <Tags products={tagInfo.products} ref={componentRef} />
            </div>

            {showScanner && (
                <BarcodeScanner
                    open={showScanner}
                    onClose={() => { setShowScanner(false); setScanFeedback(null); }}
                    onScan={handleScan}
                    continuous
                    paused={lookingUp}
                    feedback={scanFeedback}
                    title={t('products.scanTagsToPrint')}
                >
                    <Grid item>
                        <Typography>
                            {t('products.itemsScanned', { count: barcodes.filter(code => code !== '').length })}
                        </Typography>
                        {barcodes.filter(code => code !== '').map(code => (
                            <Typography key={code} variant='body2'>{code}</Typography>
                        ))}
                    </Grid>
                    <Grid item>
                        <Button variant='contained' onClick={() => setShowScanner(false)}>
                            <Typography>{t('common.done')}</Typography>
                        </Button>
                    </Grid>
                </BarcodeScanner>
            )}
        </>
    )
}
